import { createRouter, createWebHistory } from "vue-router";
import Dashboard from "../views/Dashboard.vue";
import Tables from "../views/Tables.vue";
import Billing from "../views/Billing.vue";
import RTL from "../views/Rtl.vue";
import Notifications from "../views/Notifications.vue";
import Profile from "../views/Profile.vue";
import SignIn from "../views/SignIn.vue";
import SignUp from "../views/SignUp.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import UserMenu from "../views/UserMenu.vue";
import MyAssignments from "../views/MyAssignments.vue";
import AdminTool from "../views/AdminTool.vue";
import Extras from "../views/Extras.vue";
import MessageTracker from "../views/MessageTracker.vue";
import Setup from "../views/Setup.vue";
import store from "../store";
import ChangePassword from "../views/ChangePassword.vue";
import RequestAssistance from "../views/RequestAssistance.vue";
import JoinOrganization from "../views/JoinOrganization.vue";
import SetupOrganization from "../views/SetupOrganization.vue";
import Calendar from "../views/Calendar.vue";
import PendingRegistrations from "../modules/registrations/PendingRegistrations.vue";
import Skills from "../modules/skill/Skills.vue";
import CreateSkill from "../modules/skill/CreateSkill.vue";
import EditSkill from "../modules/skill/EditSkill.vue";
import SkilledPersonSummary from "../modules/skill/SkilledPersonSummary.vue";
import EditInstanceWorkgroup from "../modules/instance-workgroup/EditInstanceWorkgroup.vue";
import ViewLogs from "../modules/logs/ViewLogs.vue";
import MessagingCenter from "../modules/messaging/MessagingCenter.vue";
import SignupForm from "../modules/signup-form/SignupForm.vue";
import ApiList from "../modules/api-list/ApiList.vue";
import ManageOrganization from "../modules/organizations/ManageOrganization.vue";
import EditOrganization from "../modules/organizations/EditOrganization.vue";
import EditLocation from "../modules/locations/EditLocation.vue";
import AddLocation from "../modules/locations/AddLocation.vue";
import Users from "../modules/users/Users.vue";
import UsersSummary from "../modules/users-summary/UsersSummary.vue";
import EditUsers from "../modules/users/EditUsers.vue";
import ActivitiesAndEvents from "../modules/activities-and-events/ActivitiesAndEvents.vue";
import { GetToken } from "@/utils/TokenHandler";
import EditActivity from "../modules/activities-and-events/EditActivity.vue";
import EditWorkgroup from "../modules/workgroups/EditWorkgroup.vue";
import EditActivityInstance from "../modules/activity-instance/EditActivityInstance.vue";
import AllSchedules from "../modules/all-schedules/AllSchedules.vue";
import ViewOrganization from "../modules/organizations/ViewOrganization.vue";
import NotFound from "../views/NotFound.vue";
import SessionExpired from "../views/SessionExpired.vue";
import SwapEmailRequest from "../views/SwapEmailRequest.vue";
import SwapEmailResponse from "../views/SwapEmailResponse.vue";
import DeclineSwapEmailResponse from "../views/DeclineSwapEmailResponse.vue";
import EmailResponse from "../views/EmailResponse.vue";
import DeclineAssignment from "../views/DeclineAssignment.vue";
import AssignmentConfirmation from "../views/AssignmentConfirmation.vue";
import VolunteerInvitation from "../views/VolunteerInvitation.vue";
import VolunteerSignupConfirmation from "../views/VolunteerSignupConfirmation.vue";
import SelectSwap from "../views/SelectSwap.vue";
import TestEmailPages from "../views/TestEmailPages.vue";
import GetcalRedirect from "../views/GetcalRedirect.vue";
import SummaryPage from "../views/SummaryPage.vue";
import VolunteerImpactReportPage from "../views/VolunteerImpactReportPage.vue";
import AdminReportModalPage from "../views/AdminReportModalPage.vue";
import QuickSetup from "../views/QuickSetup.vue";
import VolunteerServeInvitation from "../views/VolunteerServeInvitation.vue";
import VolunteerInvitationPreferences from "../views/VolunteerInvitationPreferences.vue";
import PeopleGroupInvitationAccept from "../views/PeopleGroupInvitationAccept.vue";

// Determine the best authenticated landing route from session data.
// Used as a fallback when sidebar routes haven't loaded yet.
function getSessionFallbackRoute() {
  const session = store.getters.stSessionData;
  if (session?.locationid) {
    return session.isAdminInLocation ? "/admin" : "/volunteer";
  }
  return "/setup";
}

// ------------------- ROUTES -------------------
const routes = [
  {
    path: "/",
    name: "/",
    redirect: () => {
      const defaultSidebarRoute = store.getters.stSidebarRoutes?.defaultRoute;
      if (defaultSidebarRoute) {
        return `/${defaultSidebarRoute}`;
      }
      // If sidebar routes aren't loaded yet, check for auth token to avoid
      // redirect loop between / → /sign-in → / when user is authenticated
      const token = GetToken();
      return token ? getSessionFallbackRoute() : "/sign-in";
    },
  },
  {
    path: "/sign-in",
    name: "SignIn",
    component: SignIn,
    meta: { public: true },
  },
  {
    path: "/sign-up",
    name: "SignUp",
    component: SignUp,
    meta: { public: true },
  },
  {
    path: "/serve-invitation/:token",
    name: "VolunteerServeInvitation",
    component: VolunteerServeInvitation,
    meta: { public: true },
  },
  {
    path: "/invitation-preferences/:token",
    name: "VolunteerInvitationPreferences",
    component: VolunteerInvitationPreferences,
    meta: { public: true },
  },
  {
    path: "/people-groups/invitations/accept",
    name: "PeopleGroupInvitationAccept",
    component: PeopleGroupInvitationAccept,
    meta: { public: true, requiresAuth: false },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: { public: true },
  },
  {
    path: "/admin",
    name: "Admin",
    component: Dashboard,
    meta: { requiresAuth: true, roles: ["admin"] },
  },
  {
    path: "/tables",
    name: "Tables",
    component: Tables,
    meta: { requiresAuth: true },
  },
  {
    path: "/billing",
    name: "Billing",
    component: Billing,
    meta: { requiresAuth: true },
  },
  {
    path: "/rtl-page",
    name: "RTL",
    component: RTL,
    meta: { requiresAuth: true },
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: Notifications,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: "/volunteer",
    name: "volunteer",
    component: UserMenu,
    meta: { requiresAuth: true, roles: ["admin", "volunteer"] },
  },
  {
    path: "/my-assignments",
    name: "myassignments",
    component: MyAssignments,
    meta: { requiresAuth: true },
  },
  {
    path: "/admin-tools",
    name: "admintool",
    component: AdminTool,
    meta: { requiresAuth: true },
  },
  {
    path: "/extras",
    name: "extras",
    component: Extras,
    meta: { requiresAuth: true },
  },
  {
    path: "/calendar",
    name: "calendar",
    component: Calendar,
    meta: { requiresAuth: true },
  },
  {
    path: "/message-tracker",
    name: "messagetracker",
    component: MessageTracker,
    meta: { requiresAuth: true },
  },
  {
    path: "/change-password",
    name: "changepassword",
    component: ChangePassword,
    meta: { requiresAuth: true },
  },
  {
    path: "/request-assistance",
    name: "request-assistance",
    component: RequestAssistance,
    meta: { requiresAuth: true },
  },
  {
    path: "/join-organization",
    name: "JoinOrganization",
    component: JoinOrganization,
    meta: { requiresAuth: true },
  },
  {
    path: "/setup-organization",
    name: "SetupOrganization",
    component: SetupOrganization,
    meta: { requiresAuth: true },
  },
  {
    path: "/quick-setup",
    name: "QuickSetup",
    component: QuickSetup,
    meta: { requiresAuth: true },
  },
  {
    path: "/organization/manage",
    name: "ManageOrganization",
    component: ManageOrganization,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/organization/view",
    name: "ViewOrganization",
    component: ViewOrganization,
    meta: { requiresAuth: true },
  },
  {
    path: "/organization/edit/:organizationID",
    name: "EditOrganization",
    component: EditOrganization,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/location/edit/:locationID",
    name: "EditLocation",
    component: EditLocation,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/location/add",
    name: "AddLocation",
    component: AddLocation,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/users",
    name: "Users",
    component: Users,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/users-summary",
    name: "UsersSummary",
    component: UsersSummary,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/users/edit/:userid",
    name: "EditUser",
    component: EditUsers,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/setup",
    name: "setup",
    component: Setup,
    meta: { requiresAuth: true },
  },
  {
    path: "/pending-registrations/:locationid",
    name: "pending-registrations",
    component: PendingRegistrations,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/skills/create/:locationid",
    name: "create-skill",
    component: CreateSkill,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/skills/edit/:locationId/:skillid",
    name: "editSkill",
    component: EditSkill,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/skills/:locationid",
    name: "skills",
    component: Skills,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/skills/skilled-person-summary/:locationid",
    name: "skilled-person-summary",
    component: SkilledPersonSummary,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/logs",
    name: "logs",
    component: ViewLogs,
    meta: { requiresAuth: true },
  },
  {
    path: "/messaging-center/:locationid",
    name: "messaging-center",
    component: MessagingCenter,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/messaging-center/:locationid/admins",
    name: "messaging-center-admins",
    component: MessagingCenter,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/messaging-center/:locationid/:source",
    name: "skill-messaging-center",
    component: MessagingCenter,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/messaging-center/:locationid/:source/:activityworkgroupid",
    name: "activity-workgroup-messaging-center",
    component: MessagingCenter,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/volunteer-signup/:userID/:locationID",
    name: "volunteer-signup",
    component: SignupForm,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/api-list",
    name: "api-list",
    component: ApiList,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/activities-events",
    name: "activities-events",
    component: ActivitiesAndEvents,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/activities/edit/:locationid/:activityid",
    name: "editActivity",
    component: EditActivity,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/activity-instance/edit/:locationid/:activityinstanceid",
    name: "editActivityInstance",
    component: EditActivityInstance,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/workgroup-instance/edit/:locationid/:instanceworkgroupid",
    name: "editWorkgroupInstance",
    component: EditInstanceWorkgroup,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/workgroups/edit/:locationid/:workgroupid/:skillid",
    name: "editWorkgroup",
    component: EditWorkgroup,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/activity-instance/summary/:id",
    name: "activityInstanceSummary",
    component: SummaryPage,
    meta: { requiresAuth: true, layout: "none", summaryType: "activity-instance" },
  },
  {
    path: "/activity-instance-workgroup/summary/:id",
    name: "aiwgSummary",
    component: SummaryPage,
    meta: { requiresAuth: true, layout: "none", summaryType: "aiwg" },
  },
  {
    path: "/volunteer-impact-report/:personId",
    name: "volunteerImpactReport",
    component: VolunteerImpactReportPage,
    meta: { requiresAuth: true, layout: "none" },
  },
  {
    path: "/admin-insights-report",
    name: "adminInsightsReport",
    component: AdminReportModalPage,
    meta: { requiresAuth: true, layout: "none", report: "insights" },
  },
  {
    path: "/admin-reports",
    name: "adminReports",
    component: AdminReportModalPage,
    meta: { requiresAuth: true, layout: "none", report: "admin" },
  },
  {
    path: "/all-schedules/:locationid",
    name: "allSchedules",
    component: AllSchedules,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    path: "/decline-assignment/:encodedString",
    name: "DeclineAssignment",
    component: DeclineAssignment,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/confirm-assignment/:encodedString",
    name: "AssignmentConfirmation",
    component: AssignmentConfirmation,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/email-response/:encodedString",
    name: "EmailResponse",
    component: EmailResponse,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/swap-email-request/:encodedString",
    name: "SwapEmailRequest",
    component: SwapEmailRequest,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/accept-swap-email-response/:encodedString",
    name: "SwapEmailResponse",
    component: SwapEmailResponse,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    // Legacy route: emails sent before the fix used /accept-swap/<token>
    path: "/accept-swap/:encodedString",
    name: "SwapEmailResponseLegacy",
    component: SwapEmailResponse,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    path: "/decline-swap-email-response/:encodedString",
    name: "DeclineSwapEmailResponse",
    component: DeclineSwapEmailResponse,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    // Legacy route: emails sent before the fix used /decline-swap/<token>
    path: "/decline-swap/:encodedString",
    name: "DeclineSwapEmailResponseLegacy",
    component: DeclineSwapEmailResponse,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    path: "/volunteer-invitation/:encodedString",
    name: "VolunteerInvitation",
    component: VolunteerInvitation,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/volunteer-signup-confirmation/:encodedString",
    name: "VolunteerSignupConfirmation",
    component: VolunteerSignupConfirmation,
    meta: { public: true, layout: "none" },
  },
  {
    // Legacy route: emails sent before the fix used /volunteer-signup/<token> (single segment)
    // This must stay so already-delivered email links keep working.
    path: "/volunteer-signup/:encodedString",
    name: "VolunteerSignupLegacy",
    component: VolunteerSignupConfirmation,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/select-swap/:encodedString",
    name: "SelectSwap",
    component: SelectSwap,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/test-email-pages",
    name: "TestEmailPages",
    component: TestEmailPages,
    meta: { public: true, layout: "none" },
  },
  {
    path: "/help-docs",
    name: "HelpDocs",
    component: () => import("../views/HelpDocs.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/Getcal/:calType/id/:encodedId",
    name: "GetcalRedirect",
    component: GetcalRedirect,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    path: "/session-expired",
    name: "SessionExpired",
    component: SessionExpired,
    meta: { public: true, requiresAuth: false, layout: "none" },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { public: true, requiresAuth: true, layout: "header" },
  }
];

// ------------------- ROUTER -------------------
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

// ------------------- AUTH GUARD -------------------
router.beforeEach(async (to, from, next) => {
  const token = GetToken();

  if (to.meta.public) {
    if (token && (to.name === "SignIn" || to.name === "SignUp")) {
      // Redirect to sidebar default or session-based fallback — avoid
      // { name: "/" } which can loop back to /sign-in when sidebar
      // routes aren't loaded yet
      const defaultRoute = store.getters.stSidebarRoutes?.defaultRoute;
      return next(defaultRoute ? `/${defaultRoute}` : getSessionFallbackRoute());
    }
    return next();
  }

  // if (to.meta.requiresAuth) {
  //   if (!token) {
  //     console.warn("No token found — redirecting to SignIn");
  //     return next({ name: "SignIn" });
  //   }
  // }

  if (to.meta.roles && token) {
    let sessionData = store.getters.stSessionData;
    if (!sessionData || Object.keys(sessionData).length === 0) {
      await store.dispatch("fetchSessionData");
      sessionData = store.getters.stSessionData;
    }

    const isAdmin = sessionData.isAdminInLocation;
    const hasLocation = sessionData.locationid;
    const userRole = isAdmin && hasLocation ? "admin" : !isAdmin && hasLocation ? "volunteer" : "user";

    if (!to.meta.roles.includes(userRole)) {
      if (userRole === "volunteer") {
        return next({ name: "volunteer" });
      } else {
        return next({ name: "setup" });
      }
    }
  }

  next();
});

// Track last active sidebar route globally
router.afterEach((to, from) => {
  const sidebarRoutes = [
    'admin',
    'volunteer',
    'admin-tools',
    'setup',
    'change-password',
    'calendar',
    'request-assistance',
    'join-organization',
    'setup-organization',
    'quick-setup',
    'help-docs'
  ];

  const currentRoute = to.path.substring(1).split('/')[0];
  const matchesSidebarRoute = sidebarRoutes.includes(currentRoute);

  if (matchesSidebarRoute) {
    store.commit('SET_LAST_ACTIVE_SIDE_NAV_ROUTE', currentRoute);
  } else {
    const lastActive = store.getters.stLastActiveSideNavRoute;
    if (!lastActive && from.path) {
      const previousRoute = from.path.substring(1).split('/')[0];
      if (sidebarRoutes.includes(previousRoute)) {
        store.commit('SET_LAST_ACTIVE_SIDE_NAV_ROUTE', previousRoute);
      }
    }
    // If lastActive exists, keep it (don't clear it when navigating to non-sidebar routes)
  }
});

export default router;